import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import SecretEditor, { secretMetadata, secretSource } from './SecretEditor';
import { v4API } from '../../Api';
import { useV4 } from './V4Layout';
import { dateTime, errorMessage } from './common';

const PURPOSES = {
  telegram_bot: 'Токен Telegram-бота V4',
  processing: 'Учётные данные процессинга',
  webhook: 'Секрет вебхука процессинга',
  callback: 'Колбек мерчанта (подпись V4)',
  legacy_callback: 'API-ключ legacy-колбека',
  receipt_storage: 'Ключ шифрования документов',
  receipt_link: 'Ключ подписанных ссылок',
};
const CREATABLE = ['processing', 'webhook', 'callback', 'legacy_callback', 'receipt_storage', 'receipt_link', 'telegram_bot'];
const SYSTEM_PURPOSES = ['receipt_storage', 'receipt_link'];

export default function Secrets() {
  const { system } = useV4();
  const [items, setItems] = useState(null);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [picker, setPicker] = useState(false);
  const [purpose, setPurpose] = useState('processing');
  const [provider, setProvider] = useState('');
  const [editor, setEditor] = useState(null);
  const generation = useRef(0);
  const load = useCallback(async () => {
    const requestId = ++generation.current;
    setLoading(true); setError('');
    try {
      const [data, described] = await Promise.all([
        v4API.secrets(),
        v4API.providers().catch(() => []),
      ]);
      if (!Array.isArray(data?.items)) throw new Error();
      if (requestId === generation.current) {
        setItems(data.items.map(secretMetadata));
        setProviders(Array.isArray(described) ? described.map(item => item?.provider).filter(name => typeof name === 'string' && name) : []);
      }
    } catch (failure) { if (requestId === generation.current) { setItems(null); setError(errorMessage(failure)); } }
    finally { if (requestId === generation.current) setLoading(false); }
  }, []);
  useEffect(() => { load(); return () => { generation.current += 1; }; }, [load, system?.settingsReady]);
  const unprepared = system?.settingsReady === false;
  const taken = new Set((items || []).filter(item => SYSTEM_PURPOSES.includes(item.purpose)).map(item => item.purpose));
  function openEditor() {
    if (!CREATABLE.includes(purpose)) return;
    setEditor({ purpose, provider: purpose === 'processing' && provider ? provider : undefined });
    setPicker(false);
  }
  return <Stack spacing={2}>
    <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap spacing={1}>
      <Typography variant="h5" component="h2">Секреты V4</Typography>
      <Stack direction="row" spacing={1}>
        <Button onClick={load} disabled={loading}>Обновить</Button>
        <Button variant="contained" onClick={() => { setPurpose('processing'); setProvider(''); setPicker(true); }} disabled={loading || unprepared}>Создать секрет</Button>
      </Stack>
    </Stack>
    <Alert severity="info">Значения зашифрованы в БД и никогда не возвращаются в браузер: доступны только метаданные. Просмотр, замена и удаление сохранённого секрета невозможны — новая версия создаётся как отдельный секрет с новой ссылкой.</Alert>
    {unprepared && <Alert severity="warning">Сначала подготовьте хранилище настроек V4 на вкладке «Система».</Alert>}
    {error && <Alert severity="error">{error}</Alert>}
    {message && <Alert severity="success" onClose={() => setMessage('')}>{message}</Alert>}
    {loading && <CircularProgress size={24} aria-label="Загрузка секретов" />}
    <Card variant="outlined"><CardContent>
      <TableContainer><Table size="small" aria-label="Секреты V4"><TableHead><TableRow>
        <TableCell>Название</TableCell><TableCell>Ссылка</TableCell><TableCell>Назначение</TableCell><TableCell>Статус</TableCell><TableCell>Источник</TableCell><TableCell>Создан</TableCell>
      </TableRow></TableHead><TableBody>
        {(items || []).map(item => <TableRow key={item.ref}>
          <TableCell sx={{ overflowWrap: 'anywhere' }}>{item.label || '—'}</TableCell>
          <TableCell sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}>{item.ref}</TableCell>
          <TableCell>{PURPOSES[item.purpose] || item.purpose}</TableCell>
          <TableCell><Chip label={item.configured ? 'Доступен' : 'Недоступен'} color={item.configured ? 'success' : 'warning'} size="small" variant="outlined" /></TableCell>
          <TableCell>{secretSource(item.source)}</TableCell>
          <TableCell>{dateTime(item.createdAt)}</TableCell>
        </TableRow>)}
        {items && !items.length && <TableRow><TableCell colSpan={6}>Секретов пока нет. Создайте первый или выполните импорт окружения на вкладке «Система».</TableCell></TableRow>}
        {!items && !loading && !error && <TableRow><TableCell colSpan={6}>Нет данных.</TableCell></TableRow>}
      </TableBody></Table></TableContainer>
    </CardContent></Card>
    <Typography variant="body2" color="text.secondary">Ссылку секрета назначайте в поле конфигурации процессинга или мерчанта (колонка «Ссылка на …»). Источник «Окружение сервера» означает, что значение пока читается из переменной окружения; перенос выполняется явным импортом на вкладке «Система».</Typography>
    <Dialog open={picker} onClose={() => setPicker(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Новый секрет</DialogTitle>
      <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
        <FormControl fullWidth>
          <InputLabel id="v4-secret-purpose">Назначение</InputLabel>
          <Select labelId="v4-secret-purpose" label="Назначение" value={purpose} onChange={event => setPurpose(event.target.value)}>
            {CREATABLE.filter(key => !taken.has(key)).map(key => <MenuItem key={key} value={key}>{PURPOSES[key]}</MenuItem>)}
          </Select>
        </FormControl>
        {purpose === 'processing' && <FormControl fullWidth>
          <InputLabel id="v4-secret-provider">Провайдер</InputLabel>
          <Select labelId="v4-secret-provider" label="Провайдер" value={provider} onChange={event => setProvider(event.target.value)}>
            <MenuItem value="">Другой (ввод JSON)</MenuItem>
            {providers.map(name => <MenuItem key={name} value={name}>{name}</MenuItem>)}
          </Select>
        </FormControl>}
        {SYSTEM_PURPOSES.includes(purpose) && <Alert severity="warning">Вводите только прежнее значение существующего ключа. Новое значение сделает старые документы и ссылки нечитаемыми.</Alert>}
      </Stack></DialogContent>
      <DialogActions><Button onClick={() => setPicker(false)}>Отмена</Button><Button variant="contained" onClick={openEditor}>Продолжить</Button></DialogActions>
    </Dialog>
    {editor && <SecretEditor purpose={editor.purpose} provider={editor.provider} title={`Новый секрет: ${PURPOSES[editor.purpose]}`}
      onClose={() => setEditor(null)}
      onCreated={item => { setEditor(null); setMessage(`Секрет создан: ${item.ref}. Значение сохранено зашифрованным и больше не отображается.`); load(); }} />}
  </Stack>;
}
